// colorOps.ts — the hand-painted colour layer of a template, as immutable ops in the style of templateOps.
//
// Colours are still GENERATED from the seed; what a designer paints is an override on top of that. Two
// things are painted, and they behave differently on purpose:
//
//   * a BOX (or one of a tunnel's spawns) — the compiler derives that box's slices from its colour, so
//     painting a box moves its slices with it and the colour books stay balanced by themselves;
//   * a SLICE — addressed by where it sits (wedge + depth, the coordinate this panel draws). This one is
//     applied last and therefore outlives the box sync, which is exactly why it can unbalance a level. The
//     backend's ColorBalance is what reports that.
//
// Every op returns a NEW template when and only when something changed. The editor reads that identity to
// decide whether to burn an undo entry — the same contract templateOps keeps.
import { MarbleColor } from "../Models/LevelData";
import { WEDGE_COUNT } from "./validateTemplate";
import { SPLIT_COLUMNS, findCell, holdsHiddenBox, isWall, splitColumnPaint, withCellPatched, withSpawnerSpawn, withSuperTunnelSpawn, withTunnelSpawn } from "./templateOps";
import { withElevatorSpawn } from "./sealOps";
// The colour painted on a box, or None when it is left to the seed. Templates load off the wire with no
// schema check, so the key really can be absent.
export function paintedCellColor(t, x, y) {
    return findCell(t, x, y)?.colorOverride ?? MarbleColor.None;
}
// Paints a box. `MarbleColor.None` hands it back to the seed.
//
// A BOX — a tunnel and a gate are refused. Those are the two cells the compiler takes no colour from: a
// tunnel's boxes are its spawns and each carries its own (withTunnelSpawnColor below), and a gate is a wall
// that drops no balls. Writing the override anyway compiled to nothing and drew no ring, so the click's
// only visible effect was the validator warning complaining about the click. Returning `t` untouched lets
// the caller fall through to "just select this one" — which is what opens the spawn list a tunnel IS
// painted from. The validator keeps its warnings: a hand-edited file can still carry either.
//
// A SPLIT box is refused for the same reason: one colour cannot paint three columns, so it is painted column
// by column from the inspector (withSplitColumnColor), which the fall-through to "select" opens.
export function withCellColor(t, x, y, color) {
    const cell = findCell(t, x, y);
    // (A propeller is a wall, but the box hidden under one is painted like any box.)
    if (!cell || (isWall(cell) && !holdsHiddenBox(cell)) || (cell.isSplit && !cell.isDouble && !cell.isPropeller) || (cell.colorOverride ?? MarbleColor.None) === color) {
        return t;
    }
    return withCellPatched(t, x, y, { colorOverride: color });
}
// Paints the box standing inside a double box; `MarbleColor.None` hands it back to the seed. The double box
// itself is painted like any box (withCellColor) — on the board, too — so the inside is painted from the
// inspector only, the way a split box's columns are.
export function withInnerColor(t, x, y, color) {
    const cell = findCell(t, x, y);
    if (!cell || isWall(cell) || !cell.isDouble) {
        return t;
    }
    return withCellPatched(t, x, y, { innerColorOverride: color });
}
// Paints one column of a split box (0 left .. 2 right); `MarbleColor.None` hands it back to the seed. Only a
// split box is painted this way. When no column is left painted the list goes back to null, the same
// "nothing painted" every template without paint carries.
export function withSplitColumnColor(t, x, y, column, color) {
    const cell = findCell(t, x, y);
    if (!cell || isWall(cell) || !cell.isSplit || column < 0 || column >= SPLIT_COLUMNS) {
        return t;
    }
    const next = Array.from({ length: SPLIT_COLUMNS }, (_, k) => (k === column ? color : splitColumnPaint(cell, k)));
    return withCellPatched(t, x, y, { splitColorOverrides: next.some((c) => c !== MarbleColor.None) ? next : null });
}
// Paints one of a tunnel's spawned boxes. The tunnel CELL is never painted: it holds no box of its own.
export function withTunnelSpawnColor(t, x, y, index, color) {
    return withTunnelSpawn(t, x, y, index, { colorOverride: color });
}
// Paints one of a super tunnel's spawned boxes — the same rule as a tunnel's: the cell itself holds no box.
export function withSuperTunnelSpawnColor(t, x, y, index, color) {
    return withSuperTunnelSpawn(t, x, y, index, { colorOverride: color });
}
// Paints one of a pipe's boxes — the tunnel's rule: the pipe's cell holds no box of its own.
export function withPipeSpawnColor(t, x, y, index, color) {
    return withSpawnerSpawn(t, x, y, "pipe", index, { colorOverride: color });
}
export function withElevatorSpawnColor(t, index, slot, color) {
    return withElevatorSpawn(t, index, slot, { colorOverride: color });
}
// Paints one of a tool box's boxes — same rule again: the crate holds no box of its own.
export function withToolBoxSpawnColor(t, x, y, index, color) {
    return withSpawnerSpawn(t, x, y, "toolBox", index, { colorOverride: color });
}
// The colour painted on the slice at (wedge, depth), or None when nobody touched it.
//
// The LAST matching entry, not the first: withSliceColor cannot write a duplicate, but a hand-edited file
// or a merge of two templates can, and LevelCompiler.ApplySliceOverrides walks the whole list writing each
// in turn — so the last one is what reaches the game. Reading the first made the grid ring a cell and name
// a colour the compiled level did not carry.
export function paintedSliceAt(t, wedge, depth) {
    let found = MarbleColor.None;
    for (const paint of t.sliceColorOverrides ?? []) {
        if (paint && paint.wedge === wedge && paint.depth === depth) {
            found = paint.color;
        }
    }
    return found;
}
// Paints the slice at (wedge, depth). Painting None REMOVES the entry rather than storing a colourless
// override — None means "no override" everywhere else, and a file full of them would mean the same thing
// while making the list grow with every undone click.
export function withSliceColor(t, wedge, depth, color) {
    if (paintedSliceAt(t, wedge, depth) === color) {
        return t;
    }
    const rest = (t.sliceColorOverrides ?? []).filter((o) => !o || o.wedge !== wedge || o.depth !== depth);
    return {
        ...t,
        sliceColorOverrides: color === MarbleColor.None ? rest : [...rest, { wedge, depth, color }],
    };
}
// Whether the slice at (wedge, depth) is frozen by hand.
export function isSliceFrozen(t, wedge, depth) {
    return (t.frozenSlices ?? []).some((f) => !!f && f.wedge === wedge && f.depth === depth);
}
// How many slices the template freezes (each spot once, however a hand-edited file repeats it).
export function frozenSliceCount(t) {
    return new Set((t.frozenSlices ?? []).filter(Boolean).map((f) => `${f.wedge}:${f.depth}`)).size;
}
// Freezes (or unfreezes) the slice at (wedge, depth). Unfreezing REMOVES every entry for that spot, so a
// duplicate in a hand-edited file cannot keep it frozen; an emptied list is stored as null, the same "no
// frozen slices" every older template carries.
export function withSliceFrozen(t, wedge, depth, frozen) {
    if (isSliceFrozen(t, wedge, depth) === frozen) {
        return t;
    }
    const rest = (t.frozenSlices ?? []).filter((f) => !!f && (f.wedge !== wedge || f.depth !== depth));
    const next = frozen ? [...rest, { wedge, depth }] : rest;
    return { ...t, frozenSlices: next.length > 0 ? next : null };
}
// Unfreezes every slice.
export function withoutFrozenSlices(t) {
    return (t.frozenSlices ?? []).length === 0 ? t : { ...t, frozenSlices: null };
}
// The slice queue as the editor draws it: one ROW per round of the deal, one COLUMN per wedge. Row 0 is the
// four slices that are active at the start of the level; each row below it is what promotes up behind them.
//
// A row can have holes. Slices are dealt round-robin, so 6 slices over 4 wedges is piles of 2/2/1/1 — the
// second row really is half empty, and drawing it that way is the only honest picture of which wedge runs
// dry first. Closing the gap would suggest a slice that does not exist.
export function sliceQueueRows(slices) {
    const piles = [];
    for (const stack of slices?.stacks ?? []) {
        if (stack && stack.index >= 0) {
            piles[stack.index] = stack.items ?? [];
        }
    }
    // Exactly the game's wedges. A hand-edited file can carry more piles, but the grid draws four columns
    // and nothing past them can reach the screen — counting those slices in the heading would promise
    // customers there is no button for.
    let deepest = 0;
    for (let w = 0; w < WEDGE_COUNT; w++) {
        deepest = Math.max(deepest, piles[w]?.length ?? 0);
    }
    const rows = [];
    for (let depth = 0; depth < deepest; depth++) {
        const row = [];
        for (let wedge = 0; wedge < WEDGE_COUNT; wedge++) {
            const slice = piles[wedge]?.[depth];
            row.push(slice ? { wedge, depth, slice } : undefined);
        }
        rows.push(row);
    }
    return rows;
}
